import { API } from "aws-amplify";
import { RoundtripTime, RoundtripTimeDate } from "../Types/Common/src/primitives";
import { FenoDeviceSerialNumber, KioskID, OrganizationID, PatientID, ProgramID, ProviderID, SeriesID } from "../Types/Common/src/ids";
import { LabelsRequest, RescreenResponse, ScreenFailRequest, ScreenFailResponse, TrialBaselinesRequest, TrialBaselinesResponse, SeriesLabelsRequest, MeasurementLabelsRequest, SeriesLabelsResponse, MeasurementLabelsResponse, OrganizationTrialResponse, PatientTrialResponse } from "../Types/Common/src/api/trial";
import { Demographics } from "../Types/Common/src/demographics";
import { AddAtHomeSpirometerID } from "../Types/FormTypes";
import { CollectionID } from "../Types/Common/src/ids";
import { ComplianceAlertType, FenoInstantQcDecision, InstantQcDecision, MeasurementPOSTBody, MeasurementPUTBody, MeasurementRangeKey, MeasurementType, OscillometryWaveformPUTBody, VersionlessRangeKey } from "../Types/Common/src/measurement";
import { OrgConsentLabel } from "../Types/Common/src/organization-consent";

const API_NAME = 'v2';
const API_PATH = '/trial';

export const getTrialDataForOrganization = (organizationID: OrganizationID): Promise<OrganizationTrialResponse> => {
  return API.get(API_NAME, API_PATH, {
    queryStringParameters: {
      organizationID
    }
  });
};

export const getTrialDataForPatient = (patientID: PatientID, startDate?: string): Promise<PatientTrialResponse> => {
  if (startDate) {
    return API.get(API_NAME, API_PATH, {
      queryStringParameters: {
        patientID,
        startDate
      }
    });
  };
  return API.get(API_NAME, API_PATH, {
    queryStringParameters: {
      patientID
    }
  });
};

// program string is display name of the program -- all lowercase, no spaces
export const getTrialProgram = (program: string): Promise<any> => {
  return API.get(API_NAME, API_PATH, {
    queryStringParameters: {
      program
    }
  });
};

export const screenFailParticipant = (patientID: PatientID, organizationID: OrganizationID, atHomeEndDate: RoundtripTimeDate): Promise<ScreenFailResponse> => {
  const body: ScreenFailRequest = {
    patientID,
    organizationID,
    atHomeEndDate,
  };
  return API.post(API_NAME, API_PATH, {
    body
  });
};

export const rescreenParticipant = (patientID: PatientID, organizationID: OrganizationID): Promise<RescreenResponse> => {
  return API.post(API_NAME, API_PATH, {
    body: {
      patientID,
      organizationID
    }
  });
};

export const updateMeasurementWithBody = (body: MeasurementPUTBody) => {
  return API.put(API_NAME, '/measurement', {
    body
  });
};
export const updateMeasurement = (patientID: PatientID, measurementID: string, providerID: ProviderID, cleared?: boolean, notes?: string): Promise<any> => {
  return updateMeasurementWithBody({
    patientID,
    measurementID,
    providerID,
    notes,
    cleared
  });
};

export const persistInstantQcDecision = (
  patientID: PatientID,
  measurementID: string,
  instantQc: InstantQcDecision,
): Promise<any> => {
  return updateMeasurementWithBody({ patientID, measurementID, instantQc });
};

export const persistFenoInstantQcDecision = (
  patientID: PatientID,
  measurementID: string,
  fenoInstantQc: FenoInstantQcDecision,
): Promise<any> => {
  return updateMeasurementWithBody({ patientID, measurementID, fenoInstantQc });
};

export const updateOscillometryWaveform = (params: OscillometryWaveformPUTBody): Promise<any> => {
  return API.put(API_NAME, '/measurement', {
    queryStringParameters: params,
  });
};

export const createComplianceAlertMeasurement = (patientID: PatientID, type: ComplianceAlertType, roundtripTime: RoundtripTime, providerID: ProviderID, notes: string, value: number): Promise<any> => {
  const body: MeasurementPOSTBody = {
    patientID,
    type,
    roundtripTime,
    providerID,
    notes,
    value
  };

  return createMeasurement(body);
};

export const createMeasurement = (body: MeasurementPOSTBody) => {
  return API.post(API_NAME, '/measurement', {
    body
  });
};

export const getMeasurementsForPatient = (patientID: PatientID, type?: MeasurementType, kioskID?: string, since?: string) => {
  const queryStringParameters: Record<string, string> = { patientID };
  if (type) queryStringParameters.type = type;
  if (kioskID) queryStringParameters.kioskID = kioskID;
  // ISO lower time bound — the backend skips history partitions before it (recent-only lookups)
  if (since) queryStringParameters.since = since;
  return API.get(API_NAME, '/measurement', { queryStringParameters });
};

export const getParticipantMeasurements = (patientID: PatientID, measurementID?: MeasurementRangeKey | VersionlessRangeKey, useVersionlessMeasurementID?: boolean, includeOlderVersions?: boolean, type?: MeasurementType) => {
  let updatedMeasurementID: MeasurementRangeKey | VersionlessRangeKey | undefined = measurementID;
  if (measurementID && useVersionlessMeasurementID) {
    if (/_V[^_]+$/.test(measurementID)) {
      updatedMeasurementID = measurementID.replace(/_V[^_]+$/, '_') as VersionlessRangeKey;
    };
  };

  const queryStringParameters: Record<string, string | boolean> = { patientID };

  if (updatedMeasurementID !== undefined) queryStringParameters.measurementID = updatedMeasurementID;
  if (includeOlderVersions !== undefined) queryStringParameters.includeOlderVersions = includeOlderVersions;
  if (type !== undefined) queryStringParameters.type = type;

  return API.get(API_NAME, '/measurement', { queryStringParameters });
};

export const createEcoaMeasurement = (patientID: PatientID, providerID: ProviderID, kioskID: KioskID, roundtripTime: number | RoundtripTime, type: string, questions: any, collectionID?: CollectionID, labels?: Record<OrganizationID, OrgConsentLabel['label'][] | undefined>): Promise<{ measurementID: string }> => {
  return API.post(API_NAME, '/measurement', {
    body: {
      patientID,
      providerID,
      kioskID,
      roundtripTime,
      type,
      questions,
      collectionID,
      labels
    }
  });
};

// patientID, type, fileName
export const getReport = (patientID: PatientID, fileName: string, type?: string): Promise<any> => {
  return API.put('automatedReports', '/measurementReport', {
    body: {
      patientID,
      fileName,
      type
    }
  });
};

export const getPoll = (organizationID: OrganizationID, fileName: string, directory: string): Promise<any> => {
  return API.get(API_NAME, '/reporting/s3', {
    queryStringParameters: {
      organizationID,
      fileName,
      directory
    }
  });
};

export const setTrialLabelsOnSeries = (patientID: PatientID, organizationID: OrganizationID, seriesID: SeriesID, programID: ProgramID, labels: string[], existingConsentLabels: LabelsRequest['existingConsentLabels'], existingSeriesLabels: SeriesLabelsRequest['existingSeriesLabels']): Promise<SeriesLabelsResponse> => {
  const body: LabelsRequest = {
    patientID,
    organizationID,
    seriesID,
    programID,
    labels,
    existingConsentLabels,
    existingSeriesLabels,
  };
  return API.post(API_NAME, API_PATH, { body });
};

export const setTrialLabelsOnMeasurement = (patientID: PatientID, organizationID: OrganizationID, measurementID: MeasurementRangeKey, programID: ProgramID, labels: string[], existingConsentLabels: LabelsRequest['existingConsentLabels'], existingMeasurementLabels: MeasurementLabelsRequest['existingMeasurementLabels']): Promise<MeasurementLabelsResponse> => {
  const body: LabelsRequest = {
    patientID,
    organizationID,
    measurementID,
    programID,
    labels,
    existingConsentLabels,
    existingMeasurementLabels,
  };
  return API.post(API_NAME, API_PATH, { body });
};

export const setTrialBaselines = (patientID: PatientID, organizationID: OrganizationID, baselines: TrialBaselinesRequest['baselines'], existingBaselines: unknown[] | undefined): Promise<TrialBaselinesResponse> => {
  const body: TrialBaselinesRequest = {
    patientID,
    organizationID,
    baselines,
    existingBaselines,
  };
  return API.post(API_NAME, API_PATH, { body });
};

export const createNewParticipant = (organizationID: OrganizationID, program: ProgramID | undefined, studyID: string | null, demographics: Demographics, timezoneOffsetMs: number, spirometerID: AddAtHomeSpirometerID | undefined, fenoDeviceSerialNumber: FenoDeviceSerialNumber | undefined): Promise<any> => {
  const body = {
    organizationID,
    program,
    studyID,
    demographics,
    timezoneOffsetMs,
    spirometerID,
    fenoDeviceSerialNumber
  };
  return API.post(API_NAME, '/patient/manage', { body });
};

export const generateParticipantLinkCode = (patientID: PatientID, patientCode: string) => {
  const body = {
    patientID,
    patientCode
  };
  return API.put(API_NAME, '/otp', { body });
};